import type { Selection } from '@getaccept/editor-lib';
import type {
  PricingTableColumn,
  PricingTable,
  PricingTableRow,
  PricingTableSection,
  PricingTableSummaryValues,
} from '../../types';
import { EPricingTableColumn, SubElementType } from '../../types/enums';

export const getIsNodeSelected = (selected: Selection, node: PricingTable): boolean =>
  selected?.nodeId === node.id && !selected?.subElementType;

export const getSelectedSection = (node: PricingTable, selected: Selection): PricingTableSection =>
  node.pricingTableSections.find(section => section.id === selected?.subElementSectionId);

export const getUnSelectedSections = (
  node: PricingTable,
  selected: Selection
): PricingTableSection[] =>
  node.pricingTableSections.filter(section => section.id !== selected?.subElementSectionId);

export const getIsFirstPricingSectionSelected = (
  node: PricingTable,
  selected: Selection
): boolean =>
  node.pricingTableSections.findIndex(section => section.id === selected?.subElementSectionId) ===
  0;

export const getIsLastPricingSectionSelected = (
  node: PricingTable,
  selected: Selection
): boolean => {
  const sections = node.pricingTableSections;
  return (
    sections?.findIndex(section => section.id === selected?.subElementSectionId) ===
    sections?.length - 1
  );
};

export const getNodeRows = (node: PricingTable, selected: Selection): PricingTableRow[] =>
  getSelectedSection(node, selected)?.rows;

export const getSelectedRow = (node: PricingTable, selected: Selection): PricingTableRow =>
  getNodeRows(node, selected)?.find(row => row.id === selected?.subElementRowId);

export const getUnselectedRows = (node: PricingTable, selected: Selection): PricingTableRow[] =>
  getNodeRows(node, selected)?.filter(row => row.id !== selected?.subElementRowId);

export const getIsFirstPricingRowSelected = (node: PricingTable, selected: Selection): boolean =>
  getNodeRows(node, selected)?.findIndex(row => row.id === selected?.subElementRowId) === 0;

export const getIsLastPricingRowSelected = (node: PricingTable, selected: Selection): boolean => {
  const rows = getNodeRows(node, selected);
  return rows?.findIndex(row => row.id === selected?.subElementRowId) === rows?.length - 1;
};

export const getIsSubElementRow = (selected: Selection): boolean => {
  const selectedSubElement = selected?.subElementType;
  return selectedSubElement === SubElementType.Row || selectedSubElement === SubElementType.RowItem;
};

export const getIsSubElementSection = (selected: Selection, node: PricingTable): boolean =>
  getSelectedSection(node, selected) && selected?.subElementType === SubElementType.Section;

export const getNodeColumns = (node: PricingTable, selected: Selection): PricingTableColumn[] =>
  getSelectedSection(node, selected)?.columns;

export const getSelectedSectionSummary = (
  node: PricingTable,
  selected: Selection
): PricingTableSummaryValues => getSelectedSection(node, selected)?.sectionSummary;

export const getSelectedSummaryValues = (node: PricingTable): PricingTableSummaryValues =>
  node?.pricingTableSummaryValues;

export const getIsOnlyPricingRow = (node: PricingTable, selected: Selection): boolean =>
  getNodeRows(node, selected)?.length === 1;

export const getIsOnlyPricingSection = (node: PricingTable): boolean =>
  node.pricingTableSections.length === 1;

export const getSectionSummaryDisplayName = (node: PricingTable, selected: Selection): string =>
  getSelectedSectionSummary(node, selected)?.price?.displayName;

export const getSectionSummaryPriceEnabled = (node: PricingTable, selected: Selection): boolean =>
  getSelectedSectionSummary(node, selected)?.price?.enabled;

export const getIsSummaryTotalDiscountEnabled = (node: PricingTable): boolean =>
  getSelectedSummaryValues(node)?.discount?.enabled;

export const getIsDiscountEnabled = (node: PricingTable, selected: Selection): boolean =>
  getNodeColumns(node, selected)?.find(column => column.name === EPricingTableColumn.Discount)
    ?.enabled;

export const getIsTotalPriceEnabled = (node: PricingTable, selected: Selection): boolean =>
  getNodeColumns(node, selected)?.find(column => column.name === EPricingTableColumn.TotalPrice)
    ?.enabled;

export const getIsOptionalProductEnabled = (node: PricingTable, selected: Selection): boolean =>
  !!getSelectedRow(node, selected)?.optionalProductFieldId;

export const getIsVariableQuantityEnabled = (node: PricingTable, selected: Selection): boolean =>
  !!getSelectedRow(node, selected)?.variableQuantityFieldId;

export const getIsRowColumnDescription = (node: PricingTable, selected: Selection): boolean => {
  const selectedColumn = getNodeColumns(node, selected)?.find(
    column => column.id === selected?.subElementColumnId
  );
  if (!selectedColumn) {
    return false;
  }
  return selectedColumn.name === EPricingTableColumn.Description;
};
